import { createContext } from 'react'
import { BrandConfig, EnvConfig } from 'src/configurations'
import { ExtendLocales } from 'src/enums'
// Type-only import — erased at compile time, so this file stays free of any
// runtime Redux dependency. That matters: the localization layer
// (useTranslate, translateNode) imports the context object from here, and
// models/validators import the localization layer. If this file pulled in the
// reducer graph at runtime it would recreate the circular-import chain
// (models → localization → context → reducers → selectors → models).
import type { ConfigState as AppConfigState } from 'src/redux/reducers/data/config.reducer'

type BaseRequestHeaders = {
  headers: {
    'X-Extend-Brand': string
    'X-Extend-App-ID': string
    'X-Extend-Platform': string
    'X-Extend-Platform-Version': string
    Accept: string
    'Content-Type': string
  }
  baseURL: string
}

export type { BaseRequestHeaders }

export type ConfigContextActions = {
  /** Refreshes application configuration */
  refreshConfigContext: () => Promise<void>
  setLocale: (locale: ExtendLocales) => void
  getBaseRequestHeaders: () => BaseRequestHeaders
}

export type ConfigContextState = {
  environment: EnvConfig
  brand: BrandConfig
  locale: ExtendLocales
  appConfig: AppConfigState
}

export type ConfigContextValue = ConfigContextActions & ConfigContextState

const stubConfigContextActions: ConfigContextActions = {
  refreshConfigContext: () => {
    throw Error('ConfigContext Not Available')
  },
  setLocale: () => {
    throw Error('ConfigContext Not Available')
  },
  getBaseRequestHeaders: () => {
    throw Error('ConfigContext Not Available')
  },
}

// Mirrors src/redux/reducers/data/config.reducer initialState — kept as a
// literal here so this module never imports reducer code at runtime.
const defaultAppConfigState: AppConfigState = {
  departmentOptions: [],
  departmentOptionsLocalized: [],
  supportedLocales: [],
  supportedCurrencies: [],
  supportedIndustries: [],
  supportedIndustriesLocalized: [],
  teamSizeOptions: [],
  referralSourceOptions: [],
  referralSourceOptionsLocalized: [],
}

export const defaultConfigContextState: ConfigContextState = {
  environment:
    typeof document !== 'undefined'
      ? JSON.parse(document.getElementById('__APP_CONFIG__')?.innerHTML || '{}')
      : ({} as EnvConfig),
  brand:
    typeof document !== 'undefined'
      ? JSON.parse(
          document.getElementById('__BRAND_CONFIG__')?.innerHTML || '{}',
        )
      : ({} as BrandConfig),
  locale:
    typeof window !== 'undefined'
      ? (window.navigator.language as ExtendLocales)
      : ('en-US' as ExtendLocales),
  appConfig: defaultAppConfigState,
}

export const ConfigContext = createContext<ConfigContextValue>({
  ...defaultConfigContextState,
  ...stubConfigContextActions,
})
